import { TablePlusPlusCustomConfig } from "../components/widgets/TablePlusPlus/config-schema"
import { ShowableComponent } from "../hooks/use-dialog"
import { ComponentNamespace, publish, publishDict, PublishedDict } from "../redux/pubsub"
import PublishUtils from "./PublishUtils"
import { OpaqueSparqlResults } from "./sparqlResultHelpers"
import { SparqlStatus } from './use-sparql-query'
import { createContext, useContext } from 'react'
import { useRequiredContext } from '../utils/use-required-context'
import { ComponentConfig } from "../components/widgets/ComponentWidget/SetComponentConfig"
import { WidgetConfig } from "../components/WidgetConfiguration"
import { FilterConfig } from "../components/widgets/FilterPlusPlus/filterplusplus-model"

/**
 * Category of the configurator's Query tab. Every 'yasgui' variable lands there by default;
 * give a variable this category to show it next to the query (e.g. config that refers to its result variables).
 */
export const QUERY_CATEGORY = "Query"

/** Category of the configurator's Styling tab, for options that only change how the widget looks. */
export const STYLING_CATEGORY = "Styling"

/**
 * Data-structure of a configuration-item
 */
export type ConfigDescription<
  T extends ConfigVarTypename,
  TOptions extends ConfigOptions,
  TDefaultValue extends ConfigDefaultValue,
  TName extends string = string
> = {
  name: TName
  type: T
  inputVariant?: "radiogroup" | string // DEBT: string necessary for current JSX files
  label: string | JSX.Element
  formGroup?: string
  category?: string
  /**
   * Hidden in the configurator's basic mode (see the `basicWidgetSettingsByDefault` feature flag),
   * unless the option is set to something other than its default.
   */
  advanced?: boolean
  inlineLabel?: boolean
  showOptionIconsOnly?: boolean
  options?: TOptions
  defaultValue?: TDefaultValue
  defaultQuery?: string
  placeholder?: string
  noResultsLabel?: string
  helpComponent?: ShowableComponent<unknown, void>
  topicValueInterpolationEnabled?: boolean
}

export type WidgetDescription<
  T extends ConfigVarTypename = ConfigVarTypename,
  TOptions extends ConfigOptions = ConfigOptions,
  TDefaultValue extends ConfigDefaultValue = ConfigDefaultValue,
  TName extends string = string
> = {
  name: string
  component: React.ComponentType<any>,
  label: string,
  variables: ReadonlyArray<ConfigDescription<T, TOptions, TDefaultValue, TName>>
  hasChildAreas?: boolean
  icon?: string | ((config: any) => string)
}

export type ConfigVarTypename = 'select' | 'boolean' | 'number' | 'array' | 'json' | 'json-text' | 'yasgui'
                       | 'javascript' | 'button' | 'text' | 'textarea' | 'turtle' | 'html'
                       | 'tablepp-custom' | 'filterpp-custom' | 'component-config'
type ConfigOptions = PresentConfigOptions | undefined
export type PresentConfigOptions = ReadonlyArray<{ readonly label: string, readonly value: string }>
type DefinedConfigDefaultValue = string
type ConfigDefaultValue = DefinedConfigDefaultValue | undefined

/**
 * React-props that every widget gets
 * See ./front-end/src/components/Area.jsx#renderWidget
 */
export type BaseWidgetProps = {
  id?: string
  index: number
  onChange: (config: WidgetConfig, index: number) => void
  withinSelectedTab?: null | boolean
  panelstyle: string
  mode?: null | 'edit'
  pubsub: PublishedDict
  publish: typeof publish
  publishMultipleValues: typeof publishDict
  repoName: string
  repoURL: string
  localRepoURL?: string
  widget: string
  widgetId: string
  updateURLVariables : string[]
  pushURLVariables : string[]
  loading: boolean
  setLoading: (loading: boolean) => void
  namespace: ComponentNamespace
  closeDialog?: () => void
}

/**
 * React-props that every sparql-widget gets
 */
export type SparqlWidgetProps = {
  data?: OpaqueSparqlResults
  dataStatus: SparqlStatus
}

/**
 * React-props that every area-widget gets
 */
export type AreaWidgetProps = { areas: {}[] }

/**
 * React-props from configuration-descriptions
 */
export type ConfigurablePropsFromDescription<
  T extends ReadonlyArray<ConfigDescription<any, any, any>>
> =
  ObjectFrom<ConfigNameTuple<T>, ConfigTypeTuple<T>>

/**
 * An object from two tuples "zipped" together: a tuple containing keys and a tuple containing types
 * source: https://stackoverflow.com/a/70398429
 */
type ObjectFrom<
  N extends Record<keyof TupleToObject<T>, PropertyKey>,
  T extends readonly any[],
> =
  { [K in keyof TupleToObject<T> as N[K]]?: T[K] };

/**
 * A tuple typed as an object (by omitting keys that are commonly found in arrays)
 * source: https://stackoverflow.com/a/70398429
 */
type TupleToObject<T extends readonly any[]> = Omit<T, keyof any[]>

/**
 * A tuple of typenames from configuration-items
 */
type ConfigNameTuple<T extends ReadonlyArray<ConfigDescription<any, any, any>>> = {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  [K in keyof T]: T[K] extends ConfigDescription<infer _Typename, infer _Options, infer _DefaultValue, infer Name>
    ? Name
    : never
}

/**
 * a tuple of types from configuration-items
 */
type ConfigTypeTuple<T extends ReadonlyArray<ConfigDescription<any, any, any>>> = {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  [K in keyof T]: T[K] extends ConfigDescription<infer Typename, infer Options, infer DefaultValue, infer _Name>
    ? ConfigurablePropType<Typename, Options, DefaultValue>
    : never
}

type DefaultValue<T> = [T] extends [DefinedConfigDefaultValue] ? T : (null | undefined)

/**
 * Type from typename
 */
type ConfigurablePropType<T extends ConfigVarTypename, TOptions extends ConfigOptions, TDefaultValue extends ConfigDefaultValue> =
  T extends 'array' ? (null | undefined | string[]) :
  T extends 'boolean' ? (null | undefined | boolean) :
  T extends 'number' ? (null | undefined | string) :
  T extends 'json' ? (null | undefined | {} | []) :
  T extends 'yasgui' ? (null | undefined | string) :
  T extends 'select' ? ((TOptions extends PresentConfigOptions ? TOptions[number]['value'] : never) | DefaultValue<TDefaultValue>) :
  T extends 'text' ? (null | undefined | string) :
  T extends 'turtle' ? (null | undefined | string) :
  T extends 'html' ? (null | undefined | string) :
  T extends 'json-text' ? (null | undefined | string) :
  T extends 'tablepp-custom' ? (null | undefined | TablePlusPlusCustomConfig) :
  T extends 'filterpp-custom' ? (null | undefined | FilterConfig) :
  T extends 'component-config' ? (null | undefined | ComponentConfig) :
  never

type PublishedState = Record<string, any>

type SubscriptionState<T> =
  { isValid: false, hasChanged: boolean, current: unknown, topic: null | undefined | string } |
  { isValid: true, hasChanged: boolean, current: T, topic: string }

type SubscriptionTransformation<T> = { success: false }| { success: true, value: T }

export function getSubscription<T>(
  previousState: null | undefined | PublishedState,
  currentState: PublishedState,
  topic: null | undefined | string,
  transform: (x: unknown) => SubscriptionTransformation<T>
): SubscriptionState<T> {
  if (!topic) return { isValid: false, hasChanged: false, current: undefined, topic }

  const previous = previousState?.[topic]
  const current = currentState?.[topic]
  const hasChanged = previous !== current && current != null
  const transformation = transform(current)
  if (!transformation.success) return { isValid: false, hasChanged, current, topic }

  return {
    isValid: transformation.success,
    hasChanged: previous !== current && current != null,
    current: transformation.value,
    topic,
  }
}

export function isValidIndex(x: unknown): SubscriptionTransformation<number> {
  const str = String(x)
  const number = Number(x)
  const int = parseInt(str, 10)
  const isNotInt = int !== number
  if (x == null || Number.isNaN(number) || isNotInt) return { success: false }
  return { success: true, value: int }
}

export function isString(x: unknown): SubscriptionTransformation<string> {
  return typeof x !== 'string'
    ? { success: false }
    : { success: true, value: x }
}

export function interpolateSubscriptions(
  widget: {props: { pubsub: Record<string, unknown>}},
  strTemplate: null | undefined | string,
  outputFormat: 'boolean',
) {
    if (outputFormat === 'boolean') {
      const interpolated = PublishUtils.processStringForParameters(widget, strTemplate)
      return String(interpolated).toLowerCase() === 'true'
    }
    throw new Error('not implemented')
  }

export function isConfigValueEmpty(value: unknown): boolean {
  if (value == null) return true
  if (typeof value === 'string') return value.trim().length === 0
  if (Array.isArray(value)) return value.length === 0
  if (typeof value === 'object') return Object.keys(value).length === 0
  return false
}

export function parseValidFloat(value: string | null | undefined): number | undefined {
  if (value == null) return
  const parsed = parseFloat(value)
  if (isNaN(parsed)) return undefined
  return parsed
}

export const WidgetIdContext = createContext<string | null>(null)
export const useWidgetId = () => useRequiredContext(WidgetIdContext)
export const useMaybeWidgetId = () => useContext(WidgetIdContext)
